import {useQueryClient} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {getCampaignNetworkPrettyName} from '@/config/deliverableKinds';
import CreatorAction, {
  type CreatorActionKey,
} from '@/views/creators/components/creator-action/CreatorAction';
import CancelContractModal from '@/components/cancel-contract-modal';
import ChangeDueDateModal from '@/components/change-due-date-modal';
import CompleteContractModal from '@/components/complete-contract-modal';
import NegotiateModal from '@/components/negotiate-modal';
import WithdrawNegotiationOfferModal from '@/components/withdraw-negotiation-offer-modal';
import {CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY} from '@/hooks/queries/use-campaign-v2-creators-query';
import {UNIFIED_MESSAGES_BASE_QUERY_KEY} from '@/hooks/queries/use-unified-messages';
import type {
  CampaignCreatorApiResponse,
  CampaignCreatorCounts,
  CampaignCreatorRow,
} from '../../types/creatorTabTypes';
import useCreatorRowMenuItems from '../use-creator-row-menu-items';
import type {MenuItemConfig} from '../use-creator-row-menu-items/useCreatorRowMenuItems';

interface UseCreatorRowActionsParams {
  creator: CampaignCreatorRow;
  onActionComplete: (countAdjustment?: Partial<CampaignCreatorCounts>) => void;
  // Closes the surfacing menu (table view); the card's overflow menu self-closes.
  onCloseMenu: () => void;
}

interface UseCreatorRowActionsResult {
  menuItems: MenuItemConfig[];
  actionModals: React.ReactNode;
}

function useCreatorRowActions({
  creator,
  onActionComplete,
  onCloseMenu,
}: UseCreatorRowActionsParams): UseCreatorRowActionsResult {
  const [activeAction, setActiveAction] = useState<CreatorActionKey | null>(
    null
  );
  const [isNegotiateOpen, setIsNegotiateOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isDueDateOpen, setIsDueDateOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const {campaignId} = useParams<{campaignId: string}>();
  const queryClient = useQueryClient();

  const optimisticallyRemoveRow = () => {
    if (!creator.paidGigId) return;
    const targetId = creator.paidGigId;
    queryClient.setQueriesData<{
      pages?: {data: CampaignCreatorApiResponse[]}[];
      pageParams?: unknown[];
    }>(
      {queryKey: ['campaignV2Creators', {campaignId}], exact: false},
      (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.filter((c) => c.id !== targetId),
          })),
        };
      }
    );
  };

  const menuItems = useCreatorRowMenuItems({
    creator,
    onActionComplete,
    onClose: onCloseMenu,
    onSetActiveAction: setActiveAction,
    onNegotiate: () => setIsNegotiateOpen(true),
    onOpenCancelModal: () => setIsCancelOpen(true),
    onOpenDueDateModal: () => setIsDueDateOpen(true),
    onOpenCompleteModal: () => setIsCompleteOpen(true),
    onWithdraw: () => setIsWithdrawOpen(true),
  });

  const creatorActionProps = [
    {
      id: creator.profileId,
      name: creator.creatorName,
      avatar: creator.avatarUrl ?? '',
    },
  ];

  const actionModals = (
    <>
      {activeAction ? (
        <CreatorAction
          creators={creatorActionProps}
          activeCreatorAction={activeAction}
          close={(reason) => {
            setActiveAction(null);
            if (reason === 'success') {
              onActionComplete();
            }
          }}
        />
      ) : null}
      {isNegotiateOpen ? (
        <NegotiateModal
          open
          onClose={() => setIsNegotiateOpen(false)}
          paidGigId={creator.paidGigId}
          campaignId={campaignId}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: [UNIFIED_MESSAGES_BASE_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY],
            });
          }}
        />
      ) : null}
      {isWithdrawOpen && creator.brandNegotiationSnapshot?.brandNegotiation ? (
        <WithdrawNegotiationOfferModal
          open
          onClose={() => setIsWithdrawOpen(false)}
          brandNegotiationId={
            creator.brandNegotiationSnapshot.brandNegotiation.id
          }
          paidGigId={creator.paidGigId}
          networkPrettyName={getCampaignNetworkPrettyName(
            creator.brandNegotiationSnapshot.gigDeliverableChanges[0]
              ?.campaignNetwork
          )}
          snapshot={creator.brandNegotiationSnapshot}
          onWithdrawSuccess={() => {
            setIsWithdrawOpen(false);
            queryClient.invalidateQueries({
              queryKey: [UNIFIED_MESSAGES_BASE_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY],
            });
            onActionComplete();
          }}
        />
      ) : null}
      {isCancelOpen && creator.paidGigId ? (
        <CancelContractModal
          open
          onClose={() => setIsCancelOpen(false)}
          contractId={creator.paidGigId}
          creatorName={creator.creatorName}
          onSuccess={() => {
            optimisticallyRemoveRow();
            onActionComplete({hired: -1});
          }}
        />
      ) : null}
      {isDueDateOpen && creator.paidGigId ? (
        <ChangeDueDateModal
          open
          onClose={() => setIsDueDateOpen(false)}
          contractId={creator.paidGigId}
          currentDueDate={creator.contentDue}
          creatorName={creator.creatorName}
        />
      ) : null}
      {isCompleteOpen && creator.paidGigId ? (
        <CompleteContractModal
          open
          onClose={() => setIsCompleteOpen(false)}
          contractId={creator.paidGigId}
          creatorName={creator.creatorName}
          acceptedAssetsCount={creator.acceptedAssetsCount}
          requiredAssetsCount={creator.requiredAssetsCount}
        />
      ) : null}
    </>
  );

  return {menuItems, actionModals};
}

export default useCreatorRowActions;
